/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */


import {
  TableDataInfo,
  ValueData,
  ValueDataData,
  ValueDataObject
} from '../components/survey/question/questionInterfaces';
import { UtrValueType } from '../types/universalTracker';

/**
 * Techically this can be ValueData['input'] as well
 * that's why we need to add unit part that only exists on input
 */
export type ChangeVulueData = Pick<ValueData,
  'data' | 'table' | 'explain' | 'notApplicableType'>
  & { unit?: string, numberScale?: string };
type CheckBoxProps = Record<string, boolean | undefined>;

export interface ChangeProps {
  currentValueData: ChangeVulueData;
  nextValueData: ChangeVulueData;
  displayCheckbox: CheckBoxProps;
  table?: TableDataInfo;
  valueType: UtrValueType;
}

const hasTableChanged = (props: ChangeProps) => {
  const { currentValueData, table } = props;

  const currentTable = currentValueData.table ?? [];
  const rows = table?.rows ?? [];
  const nextTable = rows.map(r => r.data);

  // Some rows are marked for removal
  if (rows.some(r => r.isRemoved)) {
    return true;
  }

  if (typeof currentTable !== typeof nextTable || currentTable.length !== nextTable.length) {
    return true;
  }

  for (const index in currentTable) {
    const current = currentTable[index];
    const nextRow = nextTable[index];

    if (typeof current !== typeof nextRow) {
      return true;
    }

    // Columns do not match
    if (current.length !== nextRow.length) {
      return true;
    }

    for (let colIndex in current) {
      const currentCol = current[colIndex];
      const nextCol = nextRow[colIndex];
      if (!nextCol || typeof currentCol !== typeof nextCol) {
        return true;
      }

      if (Array.isArray(currentCol.value)) {
        if (!Array.isArray(nextCol.value)) {
          return true;
        }
        if (currentCol.value.length !== nextCol.value.length) {
          return true;
        }
        const currentArr = currentCol.value;
        const nextArr = nextCol.value;
        return currentArr.some((v, ix) => nextArr[ix] !== v);
      }

      if (currentCol.value !== nextCol.value) {
        return true;
      }

      if (currentCol.numberScale !== nextCol.numberScale) {
        return true;
      }

      if (currentCol.unit !== nextCol.unit) {
        return true;
      }
    }
  }

  return false;
}

const getNextValueDataData = (nextValueData: ChangeVulueData, displayCheckbox: CheckBoxProps): ValueData['data'] => {
  const nextDataDta = nextValueData.data;

  if (typeof nextDataDta !== 'object') {
    return nextDataDta;
  }

  if (Array.isArray(nextDataDta)) {
    return nextDataDta.length === 0 ? undefined : [...(nextDataDta)];
  }

  const data: ValueDataObject = {};
  Object.keys(nextDataDta).filter(k => displayCheckbox[k]).forEach(k => {
    data[k] = nextDataDta[k];
  });
  return Object.keys(data).length === 0 ? undefined : data;
};

const compareArray = (nextValueDataData: ValueDataData, currentValueDataData: string[]) => {
  if (!Array.isArray(nextValueDataData)) {
    return false;
  }

  // At this point we comparing string[] vs string[]
  if (currentValueDataData.length !== nextValueDataData.length) {
    return true;
  }

  for (const k in currentValueDataData) {
    if (currentValueDataData[k] !== nextValueDataData[k]) {
      return true;
    }
  }
  return false;
};

export const hasValueDataChanged = (props: ChangeProps) => {
  const { currentValueData, nextValueData, displayCheckbox, valueType } = props;

  if (currentValueData === nextValueData) {
    return false;
  }

  if (!currentValueData || !nextValueData) {
    return true;
  }

  if (currentValueData.explain !== nextValueData.explain) {
    return true;
  }

  if (valueType === UtrValueType.Table) {
    return hasTableChanged(props);
  }

  const currentValueDataData = currentValueData.data;
  const nextValueDataData = getNextValueDataData(nextValueData, displayCheckbox);

  // TS is not smart enough to figure out that types are same after this
  if (typeof currentValueDataData !== typeof nextValueDataData) {
    return true;
  }

  // This coverts undefined or string comparison
  if (currentValueDataData === nextValueDataData) {
    return false;
  }

  if (typeof currentValueDataData === 'object' && typeof nextValueDataData === 'object') {
    if (Array.isArray(currentValueDataData)) {
      return compareArray(nextValueDataData, currentValueDataData);
    }

    const keys = Object.keys(currentValueDataData);
    const nextKeys = Object.keys(nextValueDataData);

    // Different lenth or have different keys
    if (keys.length !== nextKeys.length || keys.some(k => !nextKeys.includes(k))) {
      return true;
    }

    // Need to match on keys
    for (const k in currentValueDataData) {
      if (displayCheckbox[k] && currentValueDataData[k] !== (nextValueDataData as ValueDataObject)[k]) {
        return true; // Has changed
      }
    }
    return false; // Default for
  }

  // Defaults to true, as we could not compare it somehow
  // allow to submit the data anyway in this case.
  return true;
}
